import * as React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Localized } from "@fluent/react";
import { ValidationScenario } from "../validationProcessApi";

type Props = {
    scenario: ValidationScenario | string | null | undefined;
    size?: "small" | "medium";
    dense?: boolean;
    truncate?: boolean;
    fullWidth?: boolean;
    className?: string;
};

const SCENARIOS: Record<ValidationScenario, { id: string; fallback: string }> = {
    NEW: { id: "validation-scenario-new", fallback: "NEW" },
    REQUAL_PERIODIC: { id: "validation-scenario-requal-periodic", fallback: "REQUAL (PERIODIC)" },
    REQUAL_CHANGE: { id: "validation-scenario-requal-change", fallback: "REQUAL (CHANGE)" },
};

const SCENARIO_PALETTE_KEY: Record<ValidationScenario, "success" | "info" | "warning"> = {
    NEW: "success",
    REQUAL_PERIODIC: "info",
    REQUAL_CHANGE: "warning",
};

type PaletteKey = "primary" | "secondary" | "error" | "info" | "success" | "warning";

export const ValidationProcessScenarioChip: React.FC<Props> = ({
    scenario,
    size = "small",
    dense = true,
    truncate = true,
    fullWidth = false,
    className,
}) => {
    const t = useTheme();

    if (!scenario) return null;

    const key = scenario.toString().trim().toUpperCase() as ValidationScenario;

    const i18n =
        SCENARIOS[key] ?? { id: "validation-scenario-unknown", fallback: "UNKNOWN" };

    const paletteKey: PaletteKey =
        (SCENARIO_PALETTE_KEY[key] as PaletteKey) ?? "info";

    const isSmall = size === "small";

    const height = isSmall ? 24 : 28;
    const fontSize = isSmall ? "0.75rem" : "0.8125rem";

    const basePx = dense ? (isSmall ? 4 : 6) : (isSmall ? 6 : 8);
    const labelPl = basePx;       
    const labelPr = basePx + 4;     

    const labelPy = dense ? (isSmall ? 0.5 : 1) : (isSmall ? 1 : 2);
    const gap = dense ? 0.5 : 0.75;
    const dotSize = dense ? 7 : 8;

    const main = t.palette[paletteKey].main;
    const bgColor = alpha(main, t.palette.mode === "dark" ? 0.16 : 0.1);
    const brdColor = alpha(main, 0.5);

    return (
        <Box
            className={className}
            sx={{
                display: "inline-flex",
                alignItems: "center", 
                height,
                border: "1px solid",
                borderColor: brdColor,
                bgcolor: bgColor,
                borderRadius: 1,
                pl: `${labelPl}px`,
                pr: `${labelPr}px`,  
                boxSizing: "border-box",
                maxWidth: fullWidth ? "100%" : undefined,
                width: fullWidth ? "100%" : "auto",
                overflow: truncate ? "hidden" : "visible",
                textOverflow: truncate ? "ellipsis" : "clip",
                flex: "0 0 auto",
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    width: dotSize,
                    height: dotSize,
                    borderRadius: "50%",
                    bgcolor: main,
                    mr: gap,
                    flexShrink: 0,
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    display: "flex",    
                    alignItems: "center",
                    fontWeight: 800,
                    letterSpacing: ".02em",
                    color: t.palette.text.primary,
                    lineHeight: 1, 
                    fontSize,
                    textTransform: "uppercase",
                    whiteSpace: truncate ? "nowrap" : "normal",
                    overflow: truncate ? "hidden" : "visible",
                    textOverflow: truncate ? "ellipsis" : "clip",
                    py: `${labelPy}px`,
                }}
            >
                <Localized id={i18n.id}>
                    <span>{i18n.fallback}</span>
                </Localized>
            </Typography>
        </Box>
    );
};
